import { forwardRef, memo, PropsWithChildren, useEffect, useImperativeHandle, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { MenuThreeView } from './menu-three-view';
import GridLines from 'react-gridlines';
import { getHostDirectory } from '@tools/getHostDirectory';
import { MenuOptionProps, OptionTitle } from './menu-datas';
import { getCookies, resetCookies, setCookies } from '../cookies-manager';

const backgroundFadeIn = keyframes`
0% {
  opacity: 0;
}
100% {
  opacity: 1;
}`;

const backgroundFadeOut = keyframes`
0% {
  opacity: 1;
}
100% {
  opacity: 0;
}`;

const S = {
  Container: styled.div<{ disabled?: boolean }>`
    ${({ disabled }) => css`
      position: fixed;
      left: 0;
      bottom: 0;
      width: 100vw;
      height: 100vh;
      overflow: hidden;
      pointer-events: ${disabled ? 'none' : 'auto'};
    `}
  `,
  Background: styled.div<{ disabled?: boolean; $hide: boolean }>`
    ${({ disabled, $hide }) => css`
      visibility: ${$hide ? 'hidden' : 'visible'};
      width: 100%;
      height: 100%;
      background-color: #42679f;
      background: linear-gradient(0deg, rgb(59, 92, 141) 0%, rgba(65, 130, 176, 1) 100%);
      opacity: 0;
      animation: ${disabled ? backgroundFadeOut : backgroundFadeIn} 0.3s forwards;
    `}
  `,
  GridBackground: styled(GridLines)`
    width: 100%;
    height: 100%;
    opacity: 0.2;
  `,

  ThreeContainer: styled.div`
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
  `,
};

export type OptionsCardMenuHandle = {
  open: () => void;
};

export type OptionCardMenuProps = {
  optionTitle: OptionTitle;
};

const defaultOptionsProps: MenuOptionProps[] = [
  { title: 'Home', url: '', linkPart: 'head', blank: false, actived: true },
  { title: 'Gallery', url: 'src/gallery/', linkPart: 'body', blank: false, actived: false },
  { title: 'Projects', url: 'src/projects/', linkPart: 'goggles', blank: false, actived: false },
  { title: 'Contact', url: 'src/contact/', linkPart: 'hand-l', blank: false, actived: false },
  { title: 'Resume', url: 'src/resume/', linkPart: 'hand-r', blank: true, actived: false },
];

const MainMenuContent = forwardRef<OptionsCardMenuHandle, PropsWithChildren<OptionCardMenuProps>>(
  ({ optionTitle }, ref) => {
    const threeContainerRef = useRef<HTMLDivElement>(null);
    const [menuView, setMenuView] = useState<MenuThreeView | undefined>(undefined);
    const [isShow, setIsShow] = useState(false);
    const [canInteract, setCanInteract] = useState(true);
    const [initialized, setInitialized] = useState(false);

    useImperativeHandle(ref, () => ({
      open: async () => {
        await openMenu();
      },
    }));

    const openMenu = async () => {
      if (!menuView || !canInteract) return;

      setCanInteract(false);
      setIsShow(await menuView.show());
      setInitialized(true);
      setCanInteract(true);
    };

    const onClickClose = async () => {
      await openMenu();
    };

    const onClickOption = (linkPart: string, blank: boolean) => {
      switch (linkPart) {
        case 'body':
          setCookies('hasBody', 'true');
          break;
        case 'goggles':
          setCookies('hasGoggles', 'true');
          break;
        case 'hand-l':
          setCookies('hasHandL', 'true');
          break;
        case 'hand-r':
          setCookies('hasHandR', 'true');
          break;
      }

      window.open(
        `${getHostDirectory()}${defaultOptionsProps.find((o) => o.linkPart === linkPart)!.url}`,
        blank ? '_blank' : '_self'
      );
    };

    const onKeyup = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        resetCookies();
      }
    };

    useEffect(() => {
      if (menuView) {
        menuView.onClickOption = onClickOption;
        menuView.onClickClose = onClickClose;
        return;
      }

      //for test
      if (window.location.hostname === 'localhost') window.addEventListener('keyup', onKeyup);

      const props = defaultOptionsProps.map((option) => ({
        ...option,
        actived:
          option.actived ||
          (option.linkPart === 'body' && getCookies('hasBody') === 'true') ||
          (option.linkPart === 'goggles' && getCookies('hasGoggles') === 'true') ||
          (option.linkPart === 'hand-l' && getCookies('hasHandL') === 'true') ||
          (option.linkPart === 'hand-r' && getCookies('hasHandR') === 'true'),
      }));
      setMenuView(new MenuThreeView(threeContainerRef.current!, props, optionTitle));
    }, [menuView]);

    return (
      <S.Container disabled={!isShow}>
        <S.Background disabled={!isShow} $hide={!initialized}>
          <S.GridBackground cellWidth={22} strokeWidth={1} />
        </S.Background>
        <S.ThreeContainer ref={threeContainerRef} />
      </S.Container>
    );
  }
);

export default memo(MainMenuContent);
